import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Phone, X } from "lucide-react";

const WA = "https://wa.me/385993509595";
const TEL = "tel:+385993509595";

/**
 * Sticky floating call-to-action that becomes visible after the user scrolls
 * past the hero. Shows a WhatsApp + Phone quick action pair — the highest
 * intent conversion path for a live music service.
 */
export const FloatingCTA = ({ t }) => {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3"
          data-testid="floating-cta"
        >
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="flex flex-col items-end gap-3"
              >
                <a
                  href={WA}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="floating-whatsapp-link"
                  className="group flex items-center gap-3 bg-[#25D366] text-white pl-4 pr-5 py-3 rounded-full shadow-[0_10px_30px_-6px_rgba(0,0,0,0.5)] hover:scale-[1.03] transition-transform"
                >
                  <MessageCircle size={20} />
                  <span className="text-sm font-medium tracking-wide">{t.floating.whatsapp}</span>
                </a>
                <a
                  href={TEL}
                  data-testid="floating-phone-link"
                  className="group flex items-center gap-3 bg-gold text-navy pl-4 pr-5 py-3 rounded-full shadow-[0_10px_30px_-6px_rgba(0,0,0,0.5)] hover:scale-[1.03] transition-transform"
                >
                  <Phone size={20} />
                  <span className="text-sm font-semibold tracking-wide">{t.floating.call}</span>
                </a>
              </motion.div>
            )}
          </AnimatePresence>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="floating-cta-toggle"
            aria-label={open ? "Close" : t.floating.book_now}
            className="relative w-14 h-14 rounded-full bg-gold text-navy flex items-center justify-center shadow-[0_12px_36px_-6px_rgba(212,175,55,0.55)] hover:bg-gold-soft transition-colors"
          >
            <span className="absolute inset-0 rounded-full bg-gold animate-ping opacity-30" />
            {open ? <X size={22} /> : <MessageCircle size={22} />}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingCTA;
